import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { IFieldResponse } from '../../Models/Responses/FieldResponse';
import { PortalPopUp } from './PortalPopUp';
import { ReactComponent as AcresSvg } from '../Icons/Acres.svg';
import { ReactComponent as SoilSvg } from '../Icons/Soil.svg';
import { ReactComponent as MagnifyingGlass } from '../../../assets/images/MagnifyingGlass.svg';
import { ReactComponent as StackedLineChart } from '../../../assets/images/StackedLineChart.svg';
import { ReactComponent as SelectBoundary } from '../Icons/SelectBoundary.svg';
import { Button } from '../../../components/Button/Button';
import { WeatherDataModal } from './Modals/WeatherDataModal';
import { InsightsModal } from './Modals/InsightsModal';
import styled, { useTheme } from 'styled-components';
import { dynamicSort } from '../../Utility/Utils';
import { IGrowthStageData, IWeatherSnapshot } from '../Map';
import { INitrogenInsightsResponse } from '../../Models/Responses/NitrogenInsightsResponse';
import { ScaleLoader } from 'react-spinners';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { featureFlagSelector, Features, getFeatureFlag } from '../../store/User/ConfigSlice';
import { useDefaultTrackingState, useSimpleEvent } from '../../Utility/useAmplitude';
import { useScopedSession } from '../../../tracing/session';
import { EventStructureTags, EventWeatherTags } from '../../../tracing/EventTagNames';
import { EventFieldActivities } from '../../../tracing/EventNames';
import moment from 'moment';
import { AppDispatch, useAppSelector } from '../../store/Store';
import { UserPermission } from '../../Models/Responses/UserPermissions';
import { hasEffectivePermission } from '../../store/User/AuthSlice';
import { RootState } from '../../store/Store';
import { Select } from 'antd';
import { getFieldInfo, IFieldRequest } from '../../store/Grower/FieldThunks';

const LazyPredictiveYieldRow = lazy(() => import('./PredictiveYieldRow').then(module => ({ default: module.PredictiveYieldRow })));
const LazyTargetYieldUpdater = lazy(() => import('./TargetYieldUpdater').then(module => ({ default: module.TargetYieldUpdater })));

type SoilTexture = 'Fine'|'Medium'|'Coarse';

export interface IFieldSoilData
{
	Acres: string;
	Color: string;
	MapUnit: string;
	OverallNccpi: string;
	PercentOfTotal: string;
	SoilType: string;
	SoilTexture: string;
}

export const SeedLabelRow = styled.div`
	display: flex;
	flex-direction: row;
	margin-bottom: 2%;
`;

const SeedHybridRow = styled.div`
	display: flex;
	flex-direction: row;
	padding-left: 4%;
	padding-right: 2%;
`;

const SeedHybridParentRow = styled.div`
	display: flex;
	flex-direction: row;
	padding-left: 2%;
	padding-right: 2%;
	padding-bottom: 2px;
`;

const ColoredDot = styled.div<{ color: string }>`
	&:before {
		content:"";
		width:10px;
		height:10px;
		border-radius:50%;
		background: ${props => props.color};
		display: flex;
		margin-top: 2px;
	}
`;

interface IMapPopUpProps extends PropsFromRedux
{
	reference: HTMLDivElement | undefined;
	field: IFieldResponse;
	farmName: string;
	showSoilMapButton: boolean;
	fieldSoilData: IFieldSoilData[];
	loadSoilMap: (field: IFieldResponse) => Promise<void>;
	showFieldDetails: () => void;
	weatherData: IWeatherSnapshot[];
	growthStageData: IGrowthStageData;
	onYearChange: (year: number) => void;
	IsLoadingField: boolean;
	nitrogenInsightsData: INitrogenInsightsResponse;
	nitrogenInsightsError: string;
	onNitrogenInputsSelected: (relativeMaturity: string, plantingDate: string) => void;
}

interface IFieldSeedRow
{
	Acres: number;
	Treatment: string;
}

export const MapPopUpComponent = (props: IMapPopUpProps) =>
{
	const { reference, field, farmName, showSoilMapButton, fieldSoilData, loadSoilMap, showFieldDetails, weatherData, growthStageData, onYearChange, IsLoadingField, nitrogenInsightsData, nitrogenInsightsError, onNitrogenInputsSelected } = props;
	const authToken = useAppSelector(state => state.auth.userAuthToken);
	const [fieldSeeds, setFieldSeeds] = useState<{ [key: string]: IFieldSeedRow[] }>({});
	const [showWeatherData, setShowWeatherData] = useState<boolean>(false);
	const [showInsights, setShowInsights] = useState<boolean>(false);
	const currentYear = new Date().getFullYear();
	const [fieldYear, setFieldYear] = useState<number>(currentYear);
	const featureFlags = useSelector(featureFlagSelector);
	const showWeatherPopup = getFeatureFlag(featureFlags, Features.WeatherPopup);
	const canUseTroubleshootingTools = useSelector((state: RootState) => 
		hasEffectivePermission(state, UserPermission.CanSeeTroubleShootingTools, true)
	);
	const canUseAi = useSelector((state: RootState) => 
		hasEffectivePermission(state, UserPermission.CanSeeAiInsights, true)
	);

	const trackingState = useDefaultTrackingState();
	const session = useScopedSession(MapPopUp.name, {
		[EventStructureTags.PageContext]: 'map',
		[EventStructureTags.PageUrl]: window.location.toString()
	});
	const viewEvent = useSimpleEvent(session, EventFieldActivities.ViewWeather, trackingState);

	const theme = useTheme();

	const growthStageByDate = useMemo(() => 
	{ 
		const lookup = Object.fromEntries(Object.entries(growthStageData?.GrowthStages ?? {})
			.map(([stage, date]) => [new Date(date).toDateString(), stage]));
		if(growthStageData?.PlantingDate)
		{
			lookup[new Date(growthStageData.PlantingDate).toDateString()] = 'Planting Date';
		}
		return lookup;
	}, [growthStageData]);

	const soilTexture : SoilTexture = useMemo(() =>
	{
		if(!fieldSoilData?.length)
		{
			return undefined;
		}

		const textureWithArea = fieldSoilData.reduce((prev, current) =>
		{
			const soilTexture = current.SoilTexture?.toLowerCase()?.trim();

			if(!soilTexture?.length)
			{
				return prev;
			}

			// If the string contains "fine"
			if (soilTexture.includes('fine'))
			{
				return {
					...prev,
					Fine: (prev.Fine ?? 0) + Number(current.Acres)
				};
			}
			
			// Otherwise, if the string contains "coarse"
			if (soilTexture.includes('coarse'))
			{
				return {
					...prev,
					Coarse: (prev.Coarse ?? 0) + Number(current.Acres)
				};
			}
			
			// Otherwise, if the string contains "medium"
			if (soilTexture.includes('medium'))
			{
				return {
					...prev,
					Medium: (prev.Medium ?? 0) +  Number(current.Acres)
				};
			}

			console.error('Unknown soil texture: ', soilTexture);
			return prev;
		}, {} as Record<SoilTexture, number>);

		// Get the largest type
		const [majorType, majorArea] = Object.entries(textureWithArea).reduce((prev, [type, area]) => 
		{
			return area > prev[1] ? [type, area] : prev;
		}, ['FINE', 0]);

		return majorType as SoilTexture;
	}, [fieldSoilData]);

	useEffect(() =>
	{
		if (field && field.Seeds)
		{
			// Key is the HybridName
			const currentFieldSeeds: {[key: string]: IFieldSeedRow[]} = {};
			field.Seeds.forEach(fieldSeed =>
			{
				const hybridName = fieldSeed.HybridName ? fieldSeed.HybridName : fieldSeed.BrandName;
				// Sort the seeds
				if (!currentFieldSeeds[hybridName])
				{
					currentFieldSeeds[hybridName] = [{ Acres: fieldSeed.Area, Treatment: fieldSeed.Treatment }];
				}
				else
				{
					const foundIndex = currentFieldSeeds[hybridName].findIndex((r: IFieldSeedRow) => r.Treatment === fieldSeed.Treatment);
					if (foundIndex > -1)
					{
						return;
					}
					else
					{
						currentFieldSeeds[hybridName] =
							[...currentFieldSeeds[hybridName], { Acres: fieldSeed.Area, Treatment: fieldSeed.Treatment }];
					}
				}

			});
			setFieldSeeds({ ...currentFieldSeeds });
		}
		else
		{
			setFieldSeeds({});
		}
	}, [field]);

	const handleFieldYearChange = (selectedYear: number) =>
	{
		if (field.Id && selectedYear)
		{
			onYearChange(selectedYear);
			setFieldYear(selectedYear);
		}
	};

	return (
		field ?
			<PortalPopUp className='MapPopUp_Container' reference={reference}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						padding: '5%'
					}}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center'
						}}
					>
						<span
							className='MapPopUp_FarmName'
							style={{
								fontSize: theme.fontSizes.small,
								fontWeight: theme.fontWeights.bold,
								textAlign: 'center'
							}}
						>
							{farmName}
						</span>
						<div>
							<Select 
								aria-label="Field Year"
								value={fieldYear} 
								onChange={handleFieldYearChange} 
								disabled={IsLoadingField}
							>
								<Select.Option value={currentYear - 1}>{currentYear - 1}</Select.Option>
								<Select.Option value={currentYear}>{currentYear}</Select.Option>
							</Select>
						</div>
					</div>
					<span
						className='MapPopUp_FieldName'
						style={{
							fontSize: theme.fontSizes.xLarge,
							fontWeight: theme.fontWeights.bold
						}}
					>
						{field.Name}
					</span>
					<span className='MapPopUp_Acreage' style={{ fontSize: theme.fontSizes.small, paddingBottom: '4%' }}>
						{
							showSoilMapButton ?
								<div><AcresSvg style={{ marginRight: 5, position: 'relative', top: 3 }} />{(field.Area ?? 0).toFixed(1)} ac</div>
								:
								<div>Soil Information</div>
						}
					</span>
					<div style={{display:'flex', justifyContent: 'space-around'}}>
						<Button
							className='MapPopUp_SoilMapButton'
							variant='light'
							style={{ display: showSoilMapButton ? 'flex' : 'none', alignItems: 'center' }}
							onClick={async() => await loadSoilMap(field)}
						>
							<SoilSvg style={{ position: 'relative', marginRight: 5, fill: theme.colors.primary }} />
							<span style={{
								fontWeight: theme.fontWeights.normal,
								fontSize: theme.fontSizes.small,
								color: theme.colors.primary,
							}}>
									Soil Map
							</span>
						</Button>
						<Button
							className='MapPopUp_FieldDetailsButton'
							variant='light'
							style={{ display: showSoilMapButton ? 'none' : 'flex', alignItems: 'center' }}
							onClick={showFieldDetails}
						>
							<SelectBoundary style={{ position: 'relative', marginRight: 5, fill: theme.colors.primary }} />
							<span style={{
								fontWeight: theme.fontWeights.normal,
								fontSize: theme.fontSizes.small,
								color: theme.colors.primary,
							}}>
									Field Details
							</span>
						</Button>
						{showWeatherPopup &&
								<Button
									className='MapPopUp_FieldDetailsButton'
									variant='light'
									style={{ display: 'flex', alignItems: 'center' }}
									onClick={() => 
									{
										if(weatherData?.length)
										{
											viewEvent({
												[EventWeatherTags.ViewTab]: 'risk' // default tab
											});
											setShowWeatherData(prev => !prev);
										}
									}}
								>
									<MagnifyingGlass style={{ position: 'relative', marginRight: 5, fill: theme.colors.primary }} />
									<span style={{
										fontWeight: theme.fontWeights.normal,
										fontSize: theme.fontSizes.small,
										color: theme.colors.primary,
									}}>
										{(!!weatherData?.length) && <>Weather Data</>}								
										<ScaleLoader color={theme.colors.primary} loading={!weatherData?.length} />
									</span>
								</Button>
						}
						{canUseAi &&
							<Button
								className='MapPopUp_InsightsButton'
								variant='light'
								style={{ display: 'flex', alignItems: 'center' }}
								onClick={() => setShowInsights(prev => !prev)}
							>
								<StackedLineChart style={{ position: 'relative', marginRight: 5, fill: theme.colors.primary, stroke: theme.colors.primary }} />
								<span style={{
									fontWeight: theme.fontWeights.normal,
									fontSize: theme.fontSizes.small,
									color: theme.colors.primary,
								}}>
									Root Cause
								</span>
							</Button>
						}
					</div>
					<WeatherDataModal
						isOpen={showWeatherData}
						onClose={() => setShowWeatherData(false)}
						crop={field.CurrentCrop}
						weatherData={weatherData}
						growthStageData={growthStageData}
						soilTexture={soilTexture}
						growthStageByDate={growthStageByDate}
						viewEvent={viewEvent}
						nitrogenInsightsData={nitrogenInsightsData}
						nitrogenInsightsError={nitrogenInsightsError}
						onNitrogenInputsSelected={onNitrogenInputsSelected}
					/>
					{ 
						canUseAi && <InsightsModal
							isOpen={showInsights}
							onClose={() => setShowInsights(false)}
							entityId={field.Id}
							entityName={field.Name}
							authToken={authToken}
							persona='Seller'
							subject='Field'
							troubleshootingPermission={canUseTroubleshootingTools}
						/>
					}
					{(showSoilMapButton) ?
						<div className='MapPopUp_SeedContainer'
							style={{
								border: '1px solid',
								borderColor: theme.colors.darkGrey,
								marginTop: '3%',
								width: '100%',
								padding: '5%'
							}}
						>
							{(!IsLoadingField) ?
								<div style={{ display: 'flex', flexDirection: 'column' }}>
									<SeedLabelRow>
										<div>
											Current Crop
										</div>
										<div className='FieldCurrentCrop' style={{ marginLeft: 'auto' }}>
											{field.CurrentCrop || 'N/A'}
										</div>
									</SeedLabelRow>
									<SeedLabelRow>
										<div>
											Planting Date
										</div>
										<div className='FieldCurrentCrop' style={{ marginLeft: 'auto' }}>
											{field.PlantingDate ? moment.parseZone(field.PlantingDate).local().format('MM/DD/YYYY') : 'N/A'}
										</div>
									</SeedLabelRow>
									<Suspense fallback={<div>Loading...</div>}>
										<LazyPredictiveYieldRow field={field} />
										<LazyTargetYieldUpdater
											SeedLabelRow={SeedLabelRow}
											field={field}
											fieldYear={fieldYear}
										/>
									</Suspense>		
									{
										fieldSeeds && field.Seeds?.length > 0 && <SeedLabelRow>
											<div style={{ fontWeight: theme.fontWeights.bold }}>
												Hybrid/Variety
											</div>
											<div style={{ marginLeft: 'auto', fontWeight: theme.fontWeights.bold }}>
												Acres
											</div>
										</SeedLabelRow>
									}
									{fieldSeeds && Object.keys(fieldSeeds).sort().map((hybridName) =>
										<div key={`${field.Id}-${hybridName}`} style={{ paddingBottom: 5 }}>
											<SeedHybridParentRow
												className={`HybridParentRow_${field.Id}-${hybridName}`}
												style={{ backgroundColor: theme.colors.lightestGrey }}
											>
												<div
													style={{ fontWeight: theme.fontWeights.bold }}
													className={`HybridName_${field.Id}-${hybridName}`}
												>
													{hybridName}
												</div>
												<div
													style={{ fontWeight: theme.fontWeights.bold, marginLeft: 'auto' }}
													className={`TotalAcres_${field.Id}-${hybridName}`}
												>
													{fieldSeeds[hybridName].reduce((sum, fieldDetail) => sum + fieldDetail.Acres, 0).toFixed(2)}
												</div>
											</SeedHybridParentRow>										
											{fieldSeeds[hybridName].sort(dynamicSort('Treatment')).map((seedDetails) =>
												<SeedHybridRow
													key={`${field.Id}-${hybridName}-${seedDetails.Treatment}`}
													className={`Field_${field.Id}-${hybridName}-${seedDetails.Treatment}_SeedData`}
												>
													<div className={`SeedTreatmentName_${seedDetails.Treatment}`}>
														{seedDetails.Treatment}
													</div>
													<div className={`SeedTreatmentArea_${seedDetails.Treatment}`} style={{ marginLeft: 'auto' }}>
														{seedDetails.Acres.toFixed(2)}
													</div>
												</SeedHybridRow>
											)}
										</div>
									)}
								</div> :
								<div style={{
									position: 'relative',
									top: '50%',
									left: '40%',
									paddingTop: '30px',
									paddingBottom: '30px'
								}}>
									<ScaleLoader color={theme.colors.primary} loading={IsLoadingField} />
								</div>
							}
								
						</div>
						:
						<div className='MapPopUp_SoilDataContainer'
							style={{
								marginTop: '3%',
								width: '100%',
							}}
						>
							<div style={{ display: 'flex', flexDirection: 'column' }}>
								<div
									className='SoilDataTable_HeaderRow'
									style={{
										display: 'flex',
										flexDirection: 'row',
										fontWeight: theme.fontWeights.bold,
										paddingBottom: 5
									}}>
									<div style={{ width: 62 }}>
											NCCPI
									</div>
									<div style={{ width: 80 }}>
											Soil Zone
									</div>
									<div style={{ width: 50, textAlign: 'right' }}>
											Acres
									</div>
									<div style={{ marginLeft: 'auto' }}>
											% of Total
									</div>
								</div>
								<div
									className='SoilDataTable_DataRows'
									style={{ display: 'flex', flexDirection: 'column', maxHeight: 200, overflowY: 'auto' }}
								>
									{fieldSoilData.sort(dynamicSort('PercentOfTotal', 'descending')).map(fsd =>
										<div
											key={fsd.MapUnit}
											className={`SoilDataRow_${fsd.MapUnit}`}
											style={{
												display: 'flex',
												flexDirection: 'row',
												justifyContent: 'space-between',
												paddingBottom: 2
											}}
										>
											<div
												className='SoilData_Color_Overall'
												style={{ display: 'flex', flexDirection: 'row', width: 60 }}
											>
												<ColoredDot className='Color' color={fsd.Color} />
												<div className='Overall' style={{ paddingLeft: 4 }}>{fsd.OverallNccpi}</div>
											</div>
											<div className='SoilData_SoilType' style={{ width: 80 }}>{fsd.SoilType}</div>
											<div className='SoilData_Acres' style={{ width: 40, textAlign: 'right' }}>{fsd.Acres}</div>
											<div className='SoilData_PercentOfTotal' style={{ width: 60, textAlign: 'right' }}>{fsd.PercentOfTotal}%</div>
										</div>
									)}
								</div>
							</div>
						</div>
					}
				</div>
			</PortalPopUp>
			:
			<React.Fragment />
	);
};

const mapStateToProps = (state: RootState) => ({
	IsLoadingField: state.grower.isLoadingField
});

const mapDispatchToProps = (dispatch: AppDispatch) =>
{
	return {
		GetFieldInformationForYear: (request: IFieldRequest) => dispatch(getFieldInfo(request))
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export const MapPopUp = connector(MapPopUpComponent);
